import { ReactNode, useId, useMemo, useState } from 'react';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

const YEARS_PER_PAGE = 12;

const pageStart = (year: number) =>
  Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

type YearPickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
};

const YearPicker = (props: YearPickerProps) => {
  const {
    value,
    onChange,
    label,
    disabled,
    className,
    id,
    'aria-invalid': ariaInvalid,
  } = props;

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);

  const selectedYear = value ? Number(value) : undefined;

  const [viewStart, setViewStart] = useState(() =>
    pageStart(selectedYear ?? new Date().getFullYear()),
  );

  const isFloating = isOpen || Boolean(value);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setViewStart(pageStart(selectedYear ?? new Date().getFullYear()));
    }
    setIsOpen(open);
  };

  const onYearSelect = (year: number) => {
    onChange?.(String(year));
    setIsOpen(false);
  };

  const years = useMemo(
    () => Array.from({ length: YEARS_PER_PAGE }, (_, i) => viewStart + i),
    [viewStart],
  );

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            id={pickerId}
            variant="outline"
            className={cn(
              'h-10! w-full justify-start border-2 border-transparent! bg-transparent text-left font-normal shadow-none ring-0 outline-none hover:bg-transparent',
              'focus-visible:border-transparent! focus-visible:ring-0 aria-invalid:border-transparent! aria-invalid:ring-0',
              'dark:bg-card dark:hover:bg-card dark:border-transparent!',
              !value && 'text-muted-foreground',
            )}
            type="button"
            disabled={disabled}
            aria-invalid={ariaInvalid}
          >
            <CalendarIcon className="mr-2 size-4" />
            {value || <span>&nbsp;</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-3"
          align="start"
        >
          <div className="mb-3 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setViewStart((s) => s - YEARS_PER_PAGE)}
              type="button"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-medium">
              {`${viewStart} – ${viewStart + YEARS_PER_PAGE - 1}`}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setViewStart((s) => s + YEARS_PER_PAGE)}
              type="button"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {years.map((year) => (
              <Button
                key={year}
                variant={selectedYear === year ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
                onClick={() => onYearSelect(year)}
                type="button"
              >
                {year}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md border-2 border-solid bg-transparent px-[9px] transition-colors',
              isOpen && !ariaInvalid && 'border-primary',
              !isOpen && !ariaInvalid && 'border-input',
              ariaInvalid && 'border-destructive',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={pickerId}
            className={cn(
              'bg-card text-muted-foreground pointer-events-none absolute z-2 gap-1 rounded-xs px-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 left-[13px] -translate-y-1/2 text-xs'
                : 'top-1/2 left-[37px] -translate-y-1/2 text-sm',
              ariaInvalid ? 'text-destructive' : isOpen && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { YearPicker };
export type { YearPickerProps };
